import { type RestoreAccount } from '@repo/api-gw-sdk';
import { useEffect } from 'react';

import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import type { PreferencesKey } from '@/data/dal/preferencesKey';

import { BucketSelection } from './bucketSelection';
import { useBuckets } from './useBuckets';

interface RestoreGenericStorageProps {
  region?: string;
  prefix?: string;
  S3: { Bucket?: string };
  GCS: { Bucket?: string };
  StorageAccount: {
    AccountName?: string;
    ResourceGroup?: string;
    Container?: string;
  };
}

type BucketSelectionStepProps<T> = StepProps<
  T & { restoreAccount?: RestoreAccount }
> & {
  preferencesKey:
    | PreferencesKey.RestoreBucket
    | PreferencesKey.RestoreGcsBucket;
};

export const BucketSelectionStep = <T extends RestoreGenericStorageProps>(
  props: BucketSelectionStepProps<T>
) => {
  const { setWizardState: setBucketSelectionState } = props;
  const dal = useDAL();
  const { data: pref } = dal.preferences.useUserPref(props.preferencesKey);
  const chosenBucket = props.wizardState.S3.Bucket || pref?.value?.bucketName;
  const { buckets, isLoading, missingListPermissions } = useBuckets(
    props.wizardState.restoreAccount,
    props.wizardState.region
  );

  useEffect(() => {
    if (chosenBucket && buckets.length) {
      const availableBucket = buckets.find(
        (x) => x.bucketName === chosenBucket && x.locationType !== 'zone'
      );
      const newBucketName = availableBucket?.bucketName;

      setBucketSelectionState((state) => {
        if (state.S3.Bucket === newBucketName) {
          return state;
        }
        return {
          ...state,
          S3: newBucketName ? { Bucket: newBucketName } : {},
        };
      });
    }
  }, [buckets, chosenBucket, setBucketSelectionState]);

  return (
    <BucketSelection
      {...props.wizardState}
      restoreAccount={props.wizardState.restoreAccount}
      setBucketSelectionState={setBucketSelectionState}
      missingListPermissions={missingListPermissions}
      buckets={buckets}
      isLoading={isLoading}
    />
  );
};
